import { useMemo, useState, type FunctionComponent } from "react";
import { Alert, Collapse, Empty, Input, Segmented, Select, Skeleton, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/pl";

import { MessageInContext } from "@fitness-app/data-models/entities/Chat";

import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";
import ChatMessageFormProvider from "~/modules/Chat/components/ChatNotificationContext/ChatMessageFormContext";
import { useUpdateExerciseRecording } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/useUpdateExerciseRecording";
import ChallengeRecordingsDatePanel from "./ChallengeRecordingsDatePanel";
import { challengeVerificationLabels } from "./constants";
import {
  recordingPointsAreAutomatic,
  useChallengeParticipantsCount,
  useChallengeRecordingsSummary,
  useChallengeRecordingTasks,
  type RecordingStatusFilter,
} from "./useChallengeRecordings";

type Props = {
  challengeId: string;
};

const statusFilterOptions: { value: RecordingStatusFilter; label: string }[] = [
  { value: "submitted", label: "Nadesłane" },
  { value: "toRate", label: "Do oceny" },
  { value: "scheduled", label: "Zaplanowane" },
  { value: "all", label: "Wszystkie" },
];

const formatEventDate = (date: string): string => dayjs(date).locale("pl").format("DD.MM.YYYY (dd)");

/**
 * TRE-1794 — recordings of a challenge, grouped by the task's event dates.
 *
 * A recording task schedules one row per participant per date, so a real challenge holds thousands
 * of them and a flat list is unreadable (and unloadable). Each date is its own lazily loaded,
 * server-paginated panel, and the default filter hides the `scheduled` placeholders nobody reviews.
 */
const ChallengeRecordingsReviewInner: FunctionComponent<Props> = ({ challengeId }) => {
  const { data: tasks, isLoading } = useChallengeRecordingTasks(challengeId);
  const { data: participantsCount } = useChallengeParticipantsCount(challengeId);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<RecordingStatusFilter>("submitted");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [openDates, setOpenDates] = useState<string[] | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const task = tasks?.find((item) => item.id === selectedTaskId) ?? tasks?.[0] ?? null;
  const { data: summary } = useChallengeRecordingsSummary(challengeId, task);

  // Open the most recent date that has already happened — that is where new uploads land.
  const defaultOpenDate = useMemo(() => {
    if (!task?.eventDates.length) {
      return null;
    }
    const today = dayjs().format("YYYY-MM-DD");
    const past = task.eventDates.filter((date) => date <= today);
    return past.length ? past[past.length - 1] : task.eventDates[0];
  }, [task]);

  if (isLoading) {
    return <Skeleton active />;
  }

  if (!task) {
    return (
      <Empty description="Brak nagrań do oceny. Dodaj zadanie typu „Trening z wideo”, aby zaplanować nagrania uczestniczek." />
    );
  }

  const isAutoScored = recordingPointsAreAutomatic(task.verification);
  const totals = Object.values(summary ?? {}).reduce(
    (accumulator, entry) => ({
      submitted: accumulator.submitted + entry.submitted,
      toRate: accumulator.toRate + entry.toRate,
    }),
    { submitted: 0, toRate: 0 },
  );

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Space size={12} wrap align="center">
        {tasks && tasks.length > 1 ? (
          <Select
            value={task.id}
            onChange={(value) => {
              setSelectedTaskId(value);
              setOpenDates(null);
            }}
            style={{ minWidth: 260 }}
            options={tasks.map((item) => ({ value: item.id, label: item.title }))}
          />
        ) : (
          <Typography.Text strong>{task.title}</Typography.Text>
        )}
        <Tag color="blue">{task.points} pkt za termin</Tag>
        <Tag>Weryfikacja: {challengeVerificationLabels[task.verification] ?? task.verification}</Tag>
        <Typography.Text type="secondary">
          Nadesłane: <strong>{totals.submitted}</strong> · do oceny: <strong>{totals.toRate}</strong> ·{" "}
          {task.eventDates.length} terminów × {participantsCount ?? "—"} uczestniczek
        </Typography.Text>
      </Space>

      {/* Bez tego trenerka czyta „do oceny" jako „czeka na punkty" — a przy auto-akceptacji punkty
          siedzą w rankingu od chwili wysłania i ocena ich nie rusza. */}
      <Alert
        showIcon
        type={isAutoScored ? "warning" : "info"}
        message={isAutoScored ? "Punkty są przyznawane automatycznie" : "Punkty przyznaje ocena trenerki"}
        description={
          isAutoScored
            ? `Każde wysłane nagranie od razu daje ${task.points} pkt w rankingu — ocena jest tylko feedbackiem dla klientki i punktów nie zmienia. Żeby odebrać punkty za termin, użyj „Cofnij” (kasuje wideo, klientka może nagrać od nowa) albo „Archiwizuj” (wideo zostaje jako dowód).`
            : `Nagranie punktuje (${task.points} pkt) dopiero po wystawieniu oceny — samo wysłanie nic nie daje. Cofnięcie oceny odbiera punkty.`
        }
      />

      <Space size={12} wrap>
        <Segmented
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as RecordingStatusFilter)}
          options={statusFilterOptions}
        />
        <Input.Search
          allowClear
          value={searchInput}
          placeholder="Szukaj po imieniu, nazwisku lub e-mailu"
          style={{ width: 300 }}
          onChange={(event) => {
            setSearchInput(event.target.value);
            if (!event.target.value) {
              setSearch("");
            }
          }}
          onSearch={setSearch}
        />
      </Space>

      <Collapse
        destroyOnHidden
        activeKey={openDates ?? (defaultOpenDate ? [defaultOpenDate] : [])}
        onChange={(keys) => setOpenDates(Array.isArray(keys) ? keys : [keys])}
        items={task.eventDates.map((date) => {
          const entry = summary?.[date];
          const isFuture = date > dayjs().format("YYYY-MM-DD");

          return {
            key: date,
            label: (
              <Space size={8} wrap>
                <Typography.Text strong>{formatEventDate(date)}</Typography.Text>
                <Typography.Text type="secondary">
                  nadesłane {entry?.submitted ?? 0} / {participantsCount ?? "—"}
                </Typography.Text>
                {entry?.toRate ? <Tag color="blue">do oceny: {entry.toRate}</Tag> : null}
                {isFuture ? <Tag>termin przed nami</Tag> : null}
              </Space>
            ),
            children: (
              <ChallengeRecordingsDatePanel
                key={`${statusFilter}-${search}`}
                challengeId={challengeId}
                task={task}
                date={date}
                statusFilter={statusFilter}
                search={search}
                onPlay={setVideoUrl}
              />
            ),
          };
        })}
      />

      <VideoPlayer visible={Boolean(videoUrl)} videoUrl={videoUrl} onClose={() => setVideoUrl(null)} />
    </Space>
  );
};

const ChallengeRecordingsReview: FunctionComponent<Props> = ({ challengeId }) => {
  const { addExerciseRecordingFeedback } = useUpdateExerciseRecording();

  return (
    <ChatMessageFormProvider
      onSuccess={(id, context) => {
        if (context?.type === MessageInContext.AddRecordingFeedback) {
          return addExerciseRecordingFeedback(String(context.metadata.recordingId), id);
        }
      }}
    >
      <ChallengeRecordingsReviewInner challengeId={challengeId} />
    </ChatMessageFormProvider>
  );
};

export default ChallengeRecordingsReview;
